import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { getJobStart } from 'server/shared/helpers/getJobStart';
import { randomId } from 'server/shared/helpers/random';
import { getNewUnixTimestamp } from 'server/shared/helpers/removeTimeZoneOffset';
import { CancelReason } from 'server/shared/models/cancelReason';
import { Customer } from 'server/shared/models/customer';
import { Invitee } from 'server/shared/models/invitee';
import {
  CustomerInternalInformation,
  Job,
  JobDepartment,
} from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobTypeEnum } from 'server/shared/models/jobTypeEnum';
import { Shift } from 'server/shared/models/shift';
import { DispoUser, PoolerUser, User } from 'server/shared/models/user';
import { inviteesHaveCancelCost } from '../../helpers/inviteesHaveCancelCost';
import { CancelDialog } from '../jobs/job-edit/dialogs/cancel-dialog/cancel-dialog.component';
import { ManualCancelTimeDialogComponent } from '../jobs/job-edit/dialogs/manual-cancel-time-dialog/manual-cancel-time-dialog.component';
import { ConfirmationDialog } from '../shared/confirmation-dialog/confirmation.component';
import { CustomerService } from './customer.service';
import { ToastService } from './toast/toast.service';
import { TranslationService } from './translation.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class JobEditService implements OnDestroy {
  i18n = this.translationService.getTranslations('jobs');
  loggedInUser?: DispoUser;
  loggedInCustomer?: Customer;

  private destroyed$ = new Subject<void>();

  constructor(
    private location: Location,
    private toastService: ToastService,
    private dialog: MatDialog,
    private translationService: TranslationService,
    private router: Router,
    private userService: UserService,
    private customerService: CustomerService,
  ) {
    this.userService.readyDispo$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((u) => {
        this.loggedInUser = u;
      });
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((c) => {
        this.loggedInCustomer = c;
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  async createJob(
    job: Job,
    bookingWarningsConfirmed: boolean,
    makeSuccessToast = true,
  ): Promise<Job | string> {
    return new Promise((resolve, reject) => {
      Meteor.call(
        'createJob',
        job,
        bookingWarningsConfirmed,
        (error: Meteor.Error, result: any) => {
          if (error) {
            this.toastService.add({
              type: 'ERROR',
              message: 'jobInsertError',
            });
            reject(error);
          } else {
            if (
              result &&
              (result.warning === 'inviteeDoesNotFitCriteria' ||
                result.warning === 'inviteeInvitedTwice')
            ) {
              this.toastService.add({
                type: 'INFO',
                message: result.warning,
              });
            }
            if (makeSuccessToast) {
              this.toastService.add({
                type: 'SUCCESS',
                message: 'newJobCreated',
              });
            }
            resolve(result);
          }
        },
      );
    });
  }

  async transferInviteeToExistingJob(
    job: Job,
    newJobDepartment: JobDepartment,
    newShifts: Shift[],
    inviteeUserId: string,
    existingOpenJobId: string,
    transferReason?: string,
    informInviteeOfReason?: boolean,
    newJobType?: JobTypeEnum,
    transferDepartmentNotAssignedConfirmed?: boolean,
  ): Promise<Job | string> {
    return new Promise((resolve, reject) => {
      Meteor.call(
        'transferInviteeToExistingJob',
        job,
        newJobDepartment,
        newShifts,
        inviteeUserId,
        existingOpenJobId,
        transferReason,
        informInviteeOfReason,
        newJobType,
        transferDepartmentNotAssignedConfirmed,
        (error: Meteor.Error, result: any) => {
          if (error) {
            this.toastService.add({
              type: 'ERROR',
              message: 'jobInsertError',
            });
            reject(error);
          } else {
            this.toastService.add({
              type: 'SUCCESS',
              message: 'inviteeTransferred',
            });
            resolve(result);
          }
        },
      );
    });
  }

  async transferInvitee(
    job: Job,
    newJobDepartment: JobDepartment,
    newShifts: Shift[],
    inviteeUserId: string,
    transferReason?: string,
    informInviteeOfReason?: boolean,
    newJobType?: JobTypeEnum,
    transferDepartmentNotAssignedConfirmed?: boolean,
  ): Promise<Job | string> {
    return new Promise((resolve, reject) => {
      Meteor.call(
        'transferInvitee',
        job,
        newJobDepartment,
        newShifts,
        inviteeUserId,
        transferReason,
        informInviteeOfReason,
        newJobType,
        transferDepartmentNotAssignedConfirmed,
        (error: Meteor.Error, result: any) => {
          if (error) {
            this.toastService.add({
              type: 'ERROR',
              message: 'jobInsertError',
            });
            reject(error);
          } else {
            this.toastService.add({
              type: 'SUCCESS',
              message: 'inviteeTransferred',
            });
            resolve(result);
          }
        },
      );
    });
  }

  async updateInvitee(
    jobId: string,
    inviteeId: string,
    option: Record<string, any>,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      Meteor.call(
        'updateInvitee',
        jobId,
        inviteeId,
        option,
        (error: Meteor.Error, result: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        },
      );
    });
  }

  startCancelJob(
    job: Job,
    historyBackAfterCancel = true,
    manualCancelTime?: Date,
  ) {
    if (!job) {
      return;
    }
    const hasExternalBookedlInvitee = job.invitees.some(
      (i) =>
        i.customer.id !== this.loggedInUser?.customerId &&
        i.status === InviteeStatusEnum.BOOKED,
    );
    const shouldUseManualCancelTime =
      this.loggedInCustomer?.enableManualCancelTimestampForExternal &&
      hasExternalBookedlInvitee;

    if (shouldUseManualCancelTime && !manualCancelTime) {
      const dialogRef = this.dialog.open(ManualCancelTimeDialogComponent);
      dialogRef.afterClosed().subscribe((timestamp) => {
        if (timestamp) {
          this.startCancelJob(job, historyBackAfterCancel, timestamp);
        }
      });
      return;
    }
    let openWarningDialog = inviteesHaveCancelCost(job);
    if (openWarningDialog) {
      const confirmDialog = this.dialog.open(ConfirmationDialog, {
        data: {
          id: 'cancelJobWarning',
          title: this.i18n.cancelCostWarningTitle || 'Mögliche Stornokosten',
          text:
            this.i18n.cancelCostWarningMessage ||
            'Sie stornieren einen Mitarbeitenden innerhalb von weniger Stunden vor Dienstbeginn. Bei gewissen Vermittlern entstehen dadurch möglicherweise Stornokosten.',
        },
      });
      confirmDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.openJobCancelDialog(
            job,
            historyBackAfterCancel,
            manualCancelTime,
          );
        }
      });
    } else {
      this.openJobCancelDialog(job, historyBackAfterCancel, manualCancelTime);
    }
  }

  openJobCancelDialog(
    job: Job,
    historyBackAfterCancel: boolean,
    manualCancelTime?: Date,
  ) {
    const dialogRef = this.dialog.open(CancelDialog, {
      data: {},
    });
    dialogRef
      .afterClosed()
      .subscribe(
        (result: {
          reason: CancelReason;
          reasonDescription: string;
          informInviteeOfReason: boolean;
        }) => {
          if (result && result.reason) {
            Meteor.call('cancelJob', job._id, result, manualCancelTime, {
              wait: false,
              onResultReceived: (error: any) => {
                if (error) {
                  this.toastService.add({
                    type: 'ERROR',
                    message: 'serverError',
                  });
                  return;
                }
                //@ts-ignore
                window.dataLayer.push({
                  event: 'cancelJob',
                  jobCancelReason: result.reason.id,
                });
                this.toastService.add({
                  type: 'SUCCESS',
                  message: 'jobCanceled',
                });
                if (historyBackAfterCancel) {
                  this.location.back();
                }
              },
            });
          }
        },
      );
  }

  duplicateJob(job: Job) {
    this.router
      .navigate(['/jobs', 'new'], { queryParams: { clone: job._id } })
      .then();
  }

  /**
   * Check if users are already invitees to avoid duplicates
   */
  checkDuplicateUsers(job: Job, newInviteeUsers: User[]) {
    const inviteeUserIds = job.invitees
      .filter((invitee) => invitee.userId)
      .map((invitee) => invitee.userId);
    const duplicateUsers = inviteeUserIds.filter(
      (i1) => inviteeUserIds.filter((i2) => i1 === i2).length > 1,
    );
    if (duplicateUsers.length > 0) {
      const isNewInviteeDuplicated = newInviteeUsers.some((u) =>
        duplicateUsers.includes(u._id),
      );
      if (isNewInviteeDuplicated) {
        this.toastService.add({
          type: 'ERROR',
          message: 'inviteeInvitedTwice',
        });
        throw new Error('inviteeInvitedTwice: ' + duplicateUsers.map((d) => d));
      } else {
        Meteor.call(
          'removeDuplicatedInvitees',
          job._id,
          (error: Meteor.Error) => {
            if (error) {
              this.toastService.add({
                type: 'ERROR',
                message: 'duplicateInvitationErrorUnclearableStatus',
              });
              throw new Error(
                'duplicateInvitationErrorUnclearableStatus: ' +
                  duplicateUsers.map((d) => d),
              );
            } else {
              this.toastService.add({
                type: 'ERROR',
                message: 'duplicateInvitationError',
              });
              throw new Error(
                'duplicateInvitationError: ' + duplicateUsers.map((d) => d),
              );
            }
          },
        );
      }
    }
  }

  /**
   * Get the customer for a user (either logged in customer or shared pool customer)
   */
  getCustomerForUser(
    user: User,
    loggedInCustomer: Customer,
    sharedPoolCustomers: Customer[],
  ): { customer: Customer; isShared: boolean } {
    if (!user._id) {
      throw new Error('No user found');
    }
    if (loggedInCustomer?.pool.activeUserIds.includes(user._id)) {
      return { customer: loggedInCustomer, isShared: false };
    } else {
      const sharedPoolCustomer = sharedPoolCustomers.find((customer) =>
        customer.pool.activeUserIds.includes(user._id || ''),
      );
      if (sharedPoolCustomer) {
        return { customer: sharedPoolCustomer, isShared: true };
      } else {
        throw new Error('No customer found for user');
      }
    }
  }

  /**
   * Create an invitee object for a user
   */
  createInviteeForUser(
    user: PoolerUser,
    type: 'INVITE' | 'DIRECT_ACCEPT',
    loggedInCustomer: Customer,
    sharedPoolCustomers: Customer[],
    suggestInsteadDirectAccept: boolean = false,
  ): Invitee {
    let status = InviteeStatusEnum.OPEN;
    if (type === 'DIRECT_ACCEPT') {
      status = suggestInsteadDirectAccept
        ? InviteeStatusEnum.POSSIBLE_CANDIDATE
        : InviteeStatusEnum.BOOKED;
    }
    const userId = Meteor.userId();
    if (!userId) throw new Meteor.Error('not-authorized');
    const { customer, isShared } = this.getCustomerForUser(
      user,
      loggedInCustomer,
      sharedPoolCustomers,
    );
    const invitee: Invitee = {
      id: randomId(),
      userId: user._id,
      status,
      createdAt: getNewUnixTimestamp(),
      customer: { id: customer._id, displayName: customer.name },
      lastEditedBy: userId,
      personalData: {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        sex: user.sex,
        dateOfBirth: user.dateOfBirth,
        certificates: user.certificates,
        specialities: user.specialities,
        poolerType:
          user.customerInformation?.[loggedInCustomer._id]?.poolerType,
      },
    };
    if (isShared) {
      invitee.isSharedPooler = isShared;
    }
    return invitee;
  }

  /**
   * Update job invitees by adding selected users and direct accept users
   */
  updateInvitees(
    job: Job,
    selectedUsers: PoolerUser[],
    directAcceptUsers: PoolerUser[],
    loggedInCustomer: Customer,
    sharedPoolCustomers: Customer[],
    suggestInsteadDirectAccept: boolean = false,
  ): Job {
    this.checkDuplicateUsers(job, [...selectedUsers, ...directAcceptUsers]);

    const directAcceptedUserIds = directAcceptUsers?.map(
      (directAcceptedUser) => directAcceptedUser._id,
    );

    // Remove users from selectedUsers if they are also in directAcceptUsers
    const filteredSelectedUsers = selectedUsers?.filter((selectedUser) => {
      return !directAcceptedUserIds?.includes(selectedUser._id);
    });

    const updatedJob = { ...job };
    updatedJob.invitees = [
      ...job.invitees,
      ...filteredSelectedUsers.map((u) =>
        this.createInviteeForUser(
          u,
          'INVITE',
          loggedInCustomer,
          sharedPoolCustomers,
          suggestInsteadDirectAccept,
        ),
      ),
      ...directAcceptUsers.map((u) =>
        this.createInviteeForUser(
          u,
          'DIRECT_ACCEPT',
          loggedInCustomer,
          sharedPoolCustomers,
          suggestInsteadDirectAccept,
        ),
      ),
    ];

    return updatedJob;
  }

  /**
   * Update an existing job with new values
   */
  async updateJob(
    job: Job,
    customerInternalInformation: CustomerInternalInformation,
    loggedInCustomer: Customer,
    loggedInUser: DispoUser,
    branch?: string,
    bookingWarningsConfirmed?: boolean,
  ): Promise<Job> {
    return new Promise((resolve, reject) => {
      // Calculate transfer index if this is a transferred job
      const transferIndex = job.transfers?.findIndex(
        (transfer) => transfer.targetCustomer.id === loggedInCustomer._id,
      );

      // Get transferToMe from the job's transfers if it exists
      const transferToMe =
        transferIndex !== undefined && transferIndex > -1
          ? job.transfers?.[transferIndex]
          : undefined;

      // Handle transfer logic
      if (
        loggedInCustomer &&
        loggedInCustomer._id !== job.customer.id &&
        loggedInUser &&
        transferIndex !== undefined &&
        transferIndex > -1 &&
        job.transfers?.[transferIndex]
      ) {
        job.transfers[transferIndex].targetCustomer.editorId = loggedInUser._id;
        job.transfers[transferIndex].targetCustomer.branchId = branch;
        job.transfers[transferIndex].criteria = transferToMe?.criteria;
      } else {
        job.branch = branch;
      }

      const values = {
        specialities: job.specialities,
        invitees: job.invitees,
        description: job.description,
        certificates: job.certificates,
        sex: job.sex,
        car: job.car,
        language: job.language,
        shifts: job.shifts,
        branch: job.branch,
        caseNumber: job.caseNumber,
        transfers: job.transfers,
        updatedAt: getNewUnixTimestamp(),
        archiveAt: job.archiveAt,
        requiredUsers: job.requiredUsers,
        jobStart: getJobStart(job.shifts, job.date),
        jobType: job.jobType,
        poolerTypes: job.poolerTypes,
        allowExternalInvitees: job.allowExternalInvitees,
        customerInternalInformation: {
          [loggedInCustomer._id]: customerInternalInformation,
        },
        transferConfirmation: job.transferConfirmation,
      } as Job;

      Meteor.call(
        'updateJob',
        job._id,
        values,
        bookingWarningsConfirmed,
        (error: Meteor.Error) => {
          if (error) {
            reject(error);
          } else {
            resolve({ ...job, ...values });
          }
        },
      );
    });
  }
}
